import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import {
  getCalendarConfig,
  getCalendarToday,
  getMeetingBrief,
  type MeetingBriefContactDto,
  synthesizeMeetingBrief,
  type TodayAgendaMeetingDto,
} from '../lib/api.js';
import type { NavView } from '../lib/destinations.js';
import { formatRelative } from '../lib/format.js';

/**
 * batch-290 (calendar Phase 2) — the "Today's meetings" digest on the Today home.
 * Lists today's imported meetings with their linked contacts and the goal-#1
 * crossover: a ⚠ flag when you're meeting someone today AND owe them a reply — clicking
 * it opens that exact conversation. Renders NOTHING when calendar import is off or there
 * are no meetings today, so the Today home is unchanged for everyone else.
 */
export function TodayMeetings({
  onChangeView,
  onSelectThread,
}: {
  onChangeView: (next: NavView) => void;
  onSelectThread: (id: string | null) => void;
}) {
  const cfg = useQuery({
    queryKey: ['calendar-config'],
    queryFn: ({ signal }) => getCalendarConfig({ signal }),
    staleTime: 60_000,
  });
  const agenda = useQuery({
    queryKey: ['calendar-today'],
    queryFn: ({ signal }) => getCalendarToday({ signal }),
    enabled: cfg.data?.enabled === true,
    staleTime: 60_000,
  });

  if (!cfg.data?.enabled) return null;
  // Surface a failure instead of silently vanishing (don't equate an error with "no
  // meetings"). Loading stays invisible — it's a secondary widget that pops in.
  if (agenda.isError) {
    return (
      <section className="today-meetings" data-testid="today-meetings">
        <p className="today-meetings-flag" role="alert">
          Couldn’t load today’s meetings.
        </p>
      </section>
    );
  }
  const meetings = agenda.data?.meetings ?? [];
  if (meetings.length === 0) return null;

  const tz = agenda.data?.timezone ?? 'Asia/Dubai';
  const needsReply = agenda.data?.needs_reply_count ?? 0;
  return (
    <section className="today-meetings" data-testid="today-meetings" aria-label="Today's meetings">
      <header className="today-meetings-head">
        <h2 className="today-meetings-title">Today’s meetings</h2>
        {needsReply > 0 ? (
          <span className="today-meetings-flag">⚠ {needsReply} with an unanswered reply</span>
        ) : null}
      </header>
      <ul className="today-meetings-list">
        {meetings.map((m) => (
          <MeetingRow
            key={m.id}
            meeting={m}
            tz={tz}
            onChangeView={onChangeView}
            onSelectThread={onSelectThread}
          />
        ))}
      </ul>
    </section>
  );
}

function formatTime(iso: string | null, tz: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: tz,
    });
  } catch {
    return '';
  }
}

function MeetingRow({
  meeting,
  tz,
  onChangeView,
  onSelectThread,
}: {
  meeting: TodayAgendaMeetingDto;
  tz: string;
  onChangeView: (next: NavView) => void;
  onSelectThread: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const hasContacts = meeting.contacts.length > 0;
  return (
    <li className="today-meeting">
      <span className="today-meeting-time">
        {meeting.all_day ? 'All day' : formatTime(meeting.start_at, tz)}
      </span>
      <span className="today-meeting-body">
        <span className="today-meeting-subject">
          {meeting.subject ?? 'Meeting'}
          {meeting.is_online ? <span className="today-meeting-badge">online</span> : null}
          {hasContacts ? (
            <button
              type="button"
              className="today-meeting-brieftoggle"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              title={open ? 'Hide brief' : 'Show what you know before this meeting'}
            >
              {open ? '▾ Brief' : '▸ Brief'}
            </button>
          ) : null}
        </span>
        {hasContacts ? (
          <span className="today-meeting-contacts">
            {meeting.contacts.map((c) => {
              const name = c.name ?? 'Contact';
              const flagged = c.needs_reply && c.thread_id;
              return (
                <button
                  key={c.contact_id}
                  type="button"
                  className={`today-meeting-contact${flagged ? ' is-flagged' : ''}`}
                  // The accessible name carries the meaning (not color/emoji alone).
                  aria-label={
                    flagged
                      ? `${name} — you owe a reply, open the conversation`
                      : `${name} — open contact`
                  }
                  title={
                    flagged ? 'You owe them a reply — open the conversation' : 'Go to contacts'
                  }
                  onClick={() => {
                    if (flagged && c.thread_id) {
                      onSelectThread(c.thread_id);
                      onChangeView('unified');
                    } else {
                      onChangeView('unified-contacts');
                    }
                  }}
                >
                  {flagged ? <span aria-hidden="true">⚠ </span> : null}
                  {name}
                </button>
              );
            })}
          </span>
        ) : null}
        {open ? (
          <MeetingBriefPanel
            eventId={meeting.id}
            onSelectThread={onSelectThread}
            onChangeView={onChangeView}
          />
        ) : null}
      </span>
    </li>
  );
}

function MeetingBriefPanel({
  eventId,
  onSelectThread,
  onChangeView,
}: {
  eventId: string;
  onSelectThread: (id: string | null) => void;
  onChangeView: (next: NavView) => void;
}) {
  // Track which contacts are awaiting a fresh AI brief (so the panel polls). We key on
  // the ai_brief.created_at captured at click time; the row is "pending" until a brief
  // with a DIFFERENT timestamp lands (first synth: null → a value; re-synth: value changes).
  const [pending, setPending] = useState<Map<string, string | null>>(() => new Map());
  const brief = useQuery({
    queryKey: ['meeting-brief', eventId],
    queryFn: ({ signal }) => getMeetingBrief(eventId, { signal }),
    refetchInterval: pending.size > 0 ? 3000 : false,
  });

  const onSynthesized = useCallback((contactId: string, prev: string | null) => {
    setPending((m) => {
      const next = new Map(m);
      next.set(contactId, prev);
      return next;
    });
  }, []);
  const clearPending = useCallback((contactId: string) => {
    setPending((m) => {
      if (!m.has(contactId)) return m;
      const next = new Map(m);
      next.delete(contactId);
      return next;
    });
  }, []);

  // Reconcile `pending` against the contacts actually returned. A contact can drop out of
  // the response between polls (archived/merged while the panel is open → the API skips it),
  // which unmounts its card so it can never clear its own pending entry; without this its
  // orphaned key would keep `pending.size > 0` and poll forever. Runs before the early
  // returns to satisfy the Rules of Hooks; skips while there's no data yet (null key).
  const returnedIdsKey = brief.data
    ? brief.data.contacts.map((c) => c.contact_id).join('\x1f')
    : null;
  useEffect(() => {
    if (returnedIdsKey === null) return;
    const present = new Set(returnedIdsKey ? returnedIdsKey.split('\x1f') : []);
    setPending((m) => {
      if (m.size === 0) return m;
      let changed = false;
      const next = new Map(m);
      for (const k of [...next.keys()]) {
        if (!present.has(k)) {
          next.delete(k);
          changed = true;
        }
      }
      return changed ? next : m;
    });
  }, [returnedIdsKey]);

  if (brief.isLoading) {
    return <span className="today-brief-note">Loading brief…</span>;
  }
  if (brief.isError) {
    return (
      <span className="today-brief-note" role="alert">
        Couldn’t load the brief.
      </span>
    );
  }
  const contacts = brief.data?.contacts ?? [];
  if (contacts.length === 0) {
    return <span className="today-brief-note">No linked contacts to brief.</span>;
  }
  return (
    <div className="today-brief">
      {contacts.map((c) => (
        <BriefContactCard
          key={c.contact_id}
          eventId={eventId}
          contact={c}
          pendingPrev={pending.get(c.contact_id)}
          isPending={pending.has(c.contact_id)}
          onSynthesized={onSynthesized}
          onResolved={clearPending}
          onSelectThread={onSelectThread}
          onChangeView={onChangeView}
        />
      ))}
    </div>
  );
}

function BriefContactCard({
  eventId,
  contact: c,
  pendingPrev,
  isPending,
  onSynthesized,
  onResolved,
  onSelectThread,
  onChangeView,
}: {
  eventId: string;
  contact: MeetingBriefContactDto;
  pendingPrev: string | null | undefined;
  isPending: boolean;
  onSynthesized: (contactId: string, prev: string | null) => void;
  onResolved: (contactId: string) => void;
  onSelectThread: (id: string | null) => void;
  onChangeView: (next: NavView) => void;
}) {
  const synth = useMutation({
    mutationFn: () => synthesizeMeetingBrief(eventId, c.contact_id),
    onSuccess: () => onSynthesized(c.contact_id, c.ai_brief?.created_at ?? null),
  });
  const ai = c.ai_brief;
  // A fresh brief has landed when a terminal row exists with a timestamp different from the
  // one captured at click time → stop polling this contact. (Effect, not render, so we
  // never setState during render.)
  const landed = isPending && !!ai && ai.created_at !== (pendingPrev ?? null);
  useEffect(() => {
    if (landed) onResolved(c.contact_id);
  }, [landed, onResolved, c.contact_id]);
  const drafting = synth.isPending || isPending;
  const name = c.name ?? 'Contact';
  const facts = c.known_facts ?? [];
  return (
    <div className="today-brief-card">
      <div className="today-brief-name">{name}</div>
      {facts.length > 0 ? (
        <ul className="today-brief-facts">
          {facts.slice(0, 6).map((f) => (
            <li key={f}>{f}</li>
          ))}
        </ul>
      ) : (
        <div className="today-brief-line today-brief-muted">No saved facts yet.</div>
      )}
      <div className="today-brief-line">
        {c.thread_count > 0
          ? `${c.thread_count} conversation${c.thread_count === 1 ? '' : 's'}${
              c.last_interaction_at ? ` · last ${formatRelative(c.last_interaction_at)}` : ''
            }`
          : 'No prior conversation'}
        {c.recent_channel ? ` · ${c.recent_channel}` : ''}
      </div>
      {c.recent_activity ? (
        <div className="today-brief-line today-brief-activity">💬 {c.recent_activity}</div>
      ) : null}
      {c.prior_meeting ? (
        <div className="today-brief-line">
          📅 Met before{c.prior_meeting.subject ? ` re: ${c.prior_meeting.subject}` : ''}
          {c.prior_meeting.start_at ? ` (${formatRelative(c.prior_meeting.start_at)})` : ''}
        </div>
      ) : null}
      {c.open_follow_up_thread_id ? (
        <button
          type="button"
          className="today-brief-followup"
          onClick={() => {
            onSelectThread(c.open_follow_up_thread_id);
            onChangeView('unified');
          }}
        >
          ⚠ You owe them a reply — open the conversation
        </button>
      ) : null}
      <div className="today-brief-ai">
        {drafting ? (
          <span className="today-brief-note">Synthesizing…</span>
        ) : ai && ai.status === 'ready' && ai.text ? (
          <p className="today-brief-aitext">{ai.text}</p>
        ) : ai && ai.status === 'suppressed' ? (
          <span className="today-brief-note">
            Not enough known to brief.{' '}
            <button type="button" className="today-brief-link" onClick={() => synth.mutate()}>
              Retry
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="today-brief-synth"
            disabled={synth.isPending}
            onClick={() => synth.mutate()}
          >
            {ai && ai.status === 'failed' ? 'Retry AI brief' : '✨ AI brief'}
          </button>
        )}
        {synth.isError ? (
          <span className="today-brief-note" role="alert">
            {' '}
            ⚠ couldn’t synthesize
          </span>
        ) : null}
      </div>
    </div>
  );
}
